import isRecord from 'lib/isRecord';

import { CitationHighlight, parseCitationHighlights, parseCitationPages } from './CitationHighlight';
import Workflow from './Workflow';
import { WorkflowAction } from './WorkflowAction';

// The committed workflow value the callback writes: the enriched matches, frozen as the list
// stood when olio-ai evaluated the packet. NOT read off action.outputs — see #matches.
export const HIGH_COST_MEDS_RESULT = 'high_cost_meds_result';

// The reviewer's per-medication decisions, recorded on action.outputs by save_progress. Never
// committed as a workflow value, so the AI's finding and the human's judgment stay separately
// attributable.
export const HIGH_COST_MEDS_REVIEWS = 'high_cost_meds_reviews';

// The only decision that speaks to CONCERN. A medication is on the client's list or it isn't,
// several rows can carry it, and none of them block the referral — so the only judgment worth
// recording about a hit is that it isn't a real concern. An undismissed match already counts.
export const DISMISS = 'dismiss';

// What olio-ai found in the packet for a matched medication. Stated once per match rather than per
// candidate: a packet names an ingredient, and the several contract rows carrying that ingredient
// are what the reviewer chooses between. Every field is optional — the packet may simply not state
// a dosage, which the card renders as an em-dash.
//
// `form` is the newest of these and olio-ai does not populate it yet. api needs no change to carry
// it — ResultEnricher passes `extracted` through opaquely without enumerating its keys — so it
// arrives here for free once olio-ai extracts it, and reads as an em-dash until then.
export interface HighCostMedExtraction {
  medication?: string | null;
  dosage?: string | null;
  route?: string | null;
  form?: string | null;
  description?: string | null;
}

// One rung of a client's cost tier key, as it stood at evaluation.
export interface HighCostMedTier {
  label: string | null;
  description: string | null;
  position: number | null;
}

// One contract row a packet medication matched, frozen as it stood at evaluation so a later admin
// edit cannot rewrite what a reviewer sees. Only genericName is guaranteed — it is the one field
// required when a medication is added to a list — so everything else is nullable and the card
// renders per field.
export interface HighCostMedCandidate {
  highCostMedicationListItemId: string;
  genericName: string | null;
  brandName: string | null;
  dosage: string | null;
  route: string | null;
  form: string | null;
  tierLabel: string | null;
  tierDescription: string | null;
  // The tier's rank in the client's cost tier key, frozen as of evaluation. This is what candidates
  // and cards are ordered by: the LABEL cannot carry an order, because tier vocabularies are
  // per-contract and arbitrary ($–$$$$, Tier 1/2/3, …) — 'Tier 10' sorts before 'Tier 2', and
  // nothing says Tier 1 is the expensive end. Null for a medication with no tier configured.
  tierPosition: number | null;
  // The whole cost tier key this row is graded on, frozen with it. A label is only interpretable
  // against its own scale — "$$$" says nothing until you know the scale runs to $$$$. Recorded per
  // candidate because item resolution spans a client's lists, so two candidates of one match can
  // legitimately carry different keys.
  tierKey: HighCostMedTier[];
  addedAt: string | null;
}

// One matched medication: what the packet said, and every contract row that could be what it named.
//
// A packet names an ingredient and a contract prices it under several brand combinations at
// different tiers, so one medication routinely matches several rows. All of them surface as
// candidates on ONE card — the reviewer decides once about the medication rather than once per row.
// A single-candidate match is a card of one and renders without a chooser.
export interface HighCostMedMatch {
  // The handle a decision names this match by. Derived by api from the sorted candidate ids, so a
  // replayed callback reproduces it and a recorded decision is never orphaned.
  matchFingerprint: string;
  candidates: HighCostMedCandidate[];
  extracted: HighCostMedExtraction;
  // `pages` and `highlights` are absent on matches recorded before olio-ai sent them; the document
  // viewer then opens the source at its first page with nothing outlined.
  sources: HighCostMedSource[];
}

export type HighCostMedSource = {
  attachmentId: string;
  displayId: number;
  pages?: number[];
  highlights?: CitationHighlight[];
};

// A match with the reviewer's decisions folded in, which is what the slideout renders.
export type AdjudicatedMatch = HighCostMedMatch & {
  dismissed: boolean;
  // Which candidate the reviewer confirmed the patient is actually on. Null while the question is
  // open, and always null on a single-candidate match, where there was nothing to choose between.
  confirmedItemId: string | null;
};

export interface HighCostMedReview {
  matchFingerprint: string;
  // Null is a real value on the wire: an undo may either drop the entry or null its decision, and
  // the API accepts both. Both read as "no decision" here.
  decision: string | null;
  confirmedHighCostMedicationListItemId: string | null;
}

export interface HighCostMedCounts {
  matched: number;
  dismissed: number;
  standing: number;
}

/**
 * An untiered row has no place in the client's key at all, so it sorts after everything that does —
 * a nullish comparison would otherwise float it to the front.
 */
const tierRank = (tierPosition: number | null) => tierPosition ?? Number.MAX_SAFE_INTEGER;

/**
 * Cards in the client's own cost tier order — the same order the admin arranged the key in, so the
 * card stack mirrors the key rather than imposing an order of its own.
 *
 * Ascending `position` on purpose: nothing guarantees which end of a key is the expensive one, so
 * reading it as "most expensive first" would be an assumption about a hand-arranged list. Mirroring
 * it makes no such claim.
 *
 * A card is ranked by its BEST candidate, since a fan-out can straddle tiers and the card has to
 * take one place in the stack. `sort` is stable, so cards sharing a rank keep the order the result
 * recorded them in.
 */
function sortByTier(matches: AdjudicatedMatch[]): AdjudicatedMatch[] {
  const rank = (match: AdjudicatedMatch) =>
    Math.min(...match.candidates.map((c) => tierRank(c.tierPosition)), Number.MAX_SAFE_INTEGER);
  return [...matches].sort((a, b) => rank(a) - rank(b));
}

/**
 * Candidates within a card, by the same rule the cards themselves use.
 *
 * Deliberately NOT ranked by how well each row agrees with the packet's dosage or route: that would
 * be the system implying which one is right, and choosing is the judgment this check leaves to a
 * person. The client's own arrangement implies nothing.
 */
function sortCandidates(candidates: HighCostMedCandidate[]): HighCostMedCandidate[] {
  return [...candidates].sort((a, b) => tierRank(a.tierPosition) - tierRank(b.tierPosition));
}

/**
 * Which candidate a match has recorded as chosen.
 *
 * A confirmation naming a row this match does not offer — only reachable through another API
 * client, since the server rejects it — reads as no confirmation rather than marking a candidate
 * that isn't there. Who chose it and when is stamped by the server and read from the activity log,
 * not from here: the chooser marks the row and says nothing about the act.
 */
function confirmation(
  candidates: HighCostMedCandidate[],
  review: HighCostMedReview | undefined
): Pick<AdjudicatedMatch, 'confirmedItemId'> {
  const none = { confirmedItemId: null };
  if (candidates.length < 2 || !review) return none;

  const confirmed = candidates.find(
    (c) => c.highCostMedicationListItemId === review.confirmedHighCostMedicationListItemId
  );
  if (!confirmed) return none;

  return { confirmedItemId: confirmed.highCostMedicationListItemId };
}

function toCandidate(candidate: Record<string, unknown>): HighCostMedCandidate {
  return {
    highCostMedicationListItemId: String(candidate.highCostMedicationListItemId ?? ''),
    genericName: (candidate.genericName as string) ?? null,
    brandName: (candidate.brandName as string) ?? null,
    dosage: (candidate.dosage as string) ?? null,
    route: (candidate.route as string) ?? null,
    form: (candidate.form as string) ?? null,
    tierLabel: (candidate.tierLabel as string) ?? null,
    tierDescription: (candidate.tierDescription as string) ?? null,
    tierPosition: typeof candidate.tierPosition === 'number' ? candidate.tierPosition : null,
    tierKey: Array.isArray(candidate.tierKey)
      ? candidate.tierKey.filter(isRecord).map((tier) => ({
          label: (tier.label as string) ?? null,
          description: (tier.description as string) ?? null,
          position: typeof tier.position === 'number' ? tier.position : null,
        }))
      : [],
    addedAt: (candidate.addedAt as string) ?? null,
  };
}

/**
 * The HighCostMeds action kind. An AI check that surfaces packet medications appearing on the
 * client's high cost medication list; it always completes when the AI reports, and a clinical
 * reviewer dismisses the matches that aren't a real concern afterwards.
 *
 * The matches and the decisions live in two different places, and that split is load-bearing:
 * `WorkflowAction#record_outputs` on the API is a wholesale replace, so a save_progress carrying
 * only the reviews erases the result key from `outputs`. The matches therefore ride the COMMITTED
 * workflow value, which survives every adjudication write, and only the reviews are read from
 * outputs. Reading matches via `outputFor` would empty the slideout the moment a reviewer
 * dismissed anything. The API reads them the same way (HighCostMeds#recorded_matches).
 *
 * That is also why `matches`/`counts` take the workflow rather than being getters: a WorkflowAction
 * holds no reference to its run, and the value has to be resolved lane-contextually.
 */
export class HighCostMedsAction extends WorkflowAction {
  // The reviewer's recorded decisions. Self-contained — these DO live on this action's outputs.
  get reviews(): HighCostMedReview[] {
    const value = this.outputFor(HIGH_COST_MEDS_REVIEWS);
    if (!Array.isArray(value)) return [];
    return value.filter(isRecord).map((review) => ({
      matchFingerprint: String(review.matchFingerprint ?? ''),
      decision: typeof review.decision === 'string' ? review.decision : null,
      confirmedHighCostMedicationListItemId:
        typeof review.confirmedHighCostMedicationListItemId === 'string'
          ? review.confirmedHighCostMedicationListItemId
          : null,
    }));
  }

  // The matches this reviewer has dismissed. A dropped entry and a nulled decision both mean
  // "not dismissed", so neither lands here.
  get dismissedFingerprints(): string[] {
    return this.reviews.filter((r) => r.decision === DISMISS).map((r) => r.matchFingerprint);
  }

  // Every matched medication with its decisions folded in, in the client's cost tier order.
  matches(workflow: Workflow): AdjudicatedMatch[] {
    const value = workflow.valueFor(HIGH_COST_MEDS_RESULT, this)?.value as unknown;
    if (!Array.isArray(value)) return [];

    const dismissed = new Set(this.dismissedFingerprints);
    const reviewFor = new Map(this.reviews.map((r) => [r.matchFingerprint, r]));

    const mapped: AdjudicatedMatch[] = value.filter(isRecord).map((match) => {
      const matchFingerprint = String(match.matchFingerprint ?? '');
      const candidates = Array.isArray(match.candidates)
        ? sortCandidates(match.candidates.filter(isRecord).map(toCandidate))
        : [];

      return {
        matchFingerprint,
        candidates,
        dismissed: dismissed.has(matchFingerprint),
        // A confirmation naming a row this match does not offer — only reachable through another
        // API client, since the server rejects it — is read as no confirmation rather than
        // rendered against a candidate that isn't there.
        ...confirmation(candidates, reviewFor.get(matchFingerprint)),
        extracted: isRecord(match.extracted) ? (match.extracted as HighCostMedExtraction) : {},
        sources: Array.isArray(match.sources)
          ? match.sources.filter(isRecord).map((s) => ({
              attachmentId: String(s.attachmentId ?? ''),
              displayId: Number(s.displayId ?? 0),
              pages: parseCitationPages(s.pages),
              highlights: parseCitationHighlights(s.highlights),
            }))
          : [],
      };
    });

    return sortByTier(mapped);
  }

  // Three counts, not four: with Dismiss the only verb that speaks to concern, a medication is
  // either dismissed or standing. They count MEDICATIONS, not contract rows — a packet medication
  // matching four rows is one concern to the reviewer. Derived from the matches so a decision
  // naming a medication the check never surfaced cannot inflate the dismissed count.
  counts(workflow: Workflow): HighCostMedCounts {
    const matches = this.matches(workflow);
    const dismissed = matches.filter((m) => m.dismissed).length;
    return { matched: matches.length, dismissed, standing: matches.length - dismissed };
  }
}
